import { type SyntheticEvent, useState } from 'react';

import useResizeObserver from '~/hooks/useResizeObserver';
import type { IFormReturnData } from './useForm';
import type { FormField } from '~/types';
import { Input, Select } from '~/components/ui';
import { TextArea } from '~/components/ui';
import { Button } from '~/components/ui';

export interface IFormProps {
  onSubmit: (form: IFormReturnData) => void;
  formData: IFormReturnData;
  formFields: FormField[];
  submitLabel?: string;
  loading?: boolean;
  includeSubmitButton?: boolean;
}

export function Form({
  onSubmit,
  formData,
  formFields,
  submitLabel,
  loading,
  includeSubmitButton = true,
}: IFormProps) {
  const [formWidth, setFormWidth] = useState<number>(0);
  const ref = useResizeObserver((rect) => {
    setFormWidth(rect.width);
  });

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault?.();
    onSubmit?.(formData);
  };

  return (
    <form
      ref={ref as React.Ref<HTMLFormElement>}
      className="w-full"
      onSubmit={handleSubmit}
    >
      <div className="w-full flex flex-wrap gap-y-4 justify-between ">
        {formFields?.map((formField: FormField) => (
          <div
            key={formField.name}
            className={`relative ${
              formWidth > 650 && !formField.fullWidth ? 'w-[48%]' : 'w-[100%]'
            }`}
          >
            <FormField formField={formField} formData={formData} />
          </div>
        ))}
      </div>
      {includeSubmitButton ? (
        <div className="w-full flex mt-8">
          <div className="w-full">
            <Button
              loading={loading}
              onClick={handleSubmit}
              width={formWidth > 650 ? '10rem' : '100%'}
              type="submit"
            >
              {submitLabel || 'Submit'}
            </Button>
          </div>
        </div>
      ) : null}
    </form>
  );
}

type FormFieldProps = {
  formField: FormField;
  formData: IFormReturnData;
};
const FormField = ({ formField, formData }: FormFieldProps) => {
  switch (formField.type) {
    case 'text':
    case 'email':
    case 'password':
    case 'number':
      return (
        <div>
          <Input
            value={formData.data[formField.name] as string}
            name={formField.name}
            label={formField.label}
            placeholder={formField.placeholder}
            required={formField.required}
            type={formField.type}
            min={formField.type === 'number' ? formField.min : undefined}
            max={formField.type === 'number' ? formField.max : undefined}
            onChange={formData.onChange}
            leftIcon={formField.leftIcon}
            renderRequiredSymbol={formField.renderRequiredSymbol}
          />
          {formField.hint != null && (
            <div className="mt-1">{formField.hint}</div>
          )}
        </div>
      );
    case 'textarea':
      return (
        <TextArea
          value={formData.data[formField.name] as string}
          name={formField.name}
          label={formField.label}
          placeholder={formField.placeholder}
          required={formField.required}
          renderRequiredSymbol={formField.renderRequiredSymbol}
          onChange={formData.onChange}
          leftIcon={formField.leftIcon}
        />
      );
    case 'select':
      return (
        <Select
          value={formData.data[formField.name] as string}
          name={formField.name}
          label={formField.label}
          required={formField.required}
          renderRequiredSymbol={formField.renderRequiredSymbol}
          options={formField.options || []}
          onChange={formData.onChange}
          leftIcon={formField.leftIcon}
        />
      );
    default:
      return null;
  }
};
